/**
 * Metrics Tab - Tag coverage statistics and analytics
 */

import { Box, Typography, Paper, Grid, Card, CardContent, Chip, Alert } from '@mui/material';
import { LessonV1_SpineTagsPrediction, TagCandidate } from '../../types';
import { useMemo } from 'react';
import ConfidenceScore from '../common/ConfidenceScore';

interface MetricsTabProps {
  prediction: LessonV1_SpineTagsPrediction;
}

interface TagMetrics {
  totalTags: number;
  selectedTags: number;
  unselectedTags: number;
  selectedPercentage: number;
  tagsWithLimiters: number;
  tagsWithoutLimiters: number;
  averageConfidence: number;
  highConfidenceTags: number;
  mediumConfidenceTags: number;
  lowConfidenceTags: number;
}

export default function MetricsTab({ prediction }: MetricsTabProps) {
  // Calculate tag metrics
  const tagMetrics = useMemo((): TagMetrics => {
    // Extract tag candidates
    let tagCandidates: TagCandidate[] = [];

    if (prediction.tag_prediction_modified) {
      tagCandidates = Array.isArray(prediction.tag_prediction_modified)
        ? prediction.tag_prediction_modified.filter(tag => tag && tag.competency)
        : [];
    } else if (prediction.tag_prediction_original) {
      tagCandidates = Array.isArray(prediction.tag_prediction_original)
        ? prediction.tag_prediction_original.filter(tag => tag && tag.competency)
        : [];
    }

    const totalTags = tagCandidates.length;
    const selectedTags = tagCandidates.filter(tag => tag.competency?.selected).length;
    const unselectedTags = totalTags - selectedTags;
    const selectedPercentage = totalTags > 0 ? (selectedTags / totalTags) * 100 : 0;

    // Count tags with limiters
    const tagsWithLimiters = tagCandidates.filter(tag => {
      const hasSelectedLimiters = tag.limiter_groups?.some(group =>
        group.selected && group.limiters?.some(limiter => limiter.selected)
      );
      return hasSelectedLimiters;
    }).length;

    const tagsWithoutLimiters = selectedTags - tagsWithLimiters;

    // Calculate confidence metrics
    const confidenceScores = tagCandidates
      .filter(tag => tag.competency?.selected && typeof tag.competency.confidence_score === 'number')
      .map(tag => tag.competency!.confidence_score);

    const averageConfidence = confidenceScores.length > 0
      ? confidenceScores.reduce((sum, score) => sum + score, 0) / confidenceScores.length
      : 0;

    const highConfidenceTags = confidenceScores.filter(score => score >= 0.8).length;
    const mediumConfidenceTags = confidenceScores.filter(score => score >= 0.5 && score < 0.8).length;
    const lowConfidenceTags = confidenceScores.filter(score => score < 0.5).length;

    return {
      totalTags,
      selectedTags,
      unselectedTags,
      selectedPercentage,
      tagsWithLimiters,
      tagsWithoutLimiters,
      averageConfidence,
      highConfidenceTags,
      mediumConfidenceTags,
      lowConfidenceTags,
    };
  }, [prediction]);

  const getPercentageColor = (percentage: number) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };


  if (tagMetrics.totalTags === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Alert severity="info">
          No tag candidates available for metrics analysis.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        📊 Tag Coverage Statistics
      </Typography>

      {/* Main Metrics Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Tags */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Tags
              </Typography>
              <Typography variant="h4" component="div">
                {tagMetrics.totalTags}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Available competencies
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Selected Tags */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Selected Tags
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h4" component="div">
                  {tagMetrics.selectedTags}
                </Typography>
                <Chip
                  label={`${tagMetrics.selectedPercentage.toFixed(1)}%`}
                  size="small"
                  color={getPercentageColor(tagMetrics.selectedPercentage) as any}
                />
              </Box>
              <Typography variant="body2" color="textSecondary">
                Out of {tagMetrics.totalTags} total
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Average Confidence */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Average Confidence
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ConfidenceScore
                  score={tagMetrics.averageConfidence}
                  reasoning="Average confidence score across all selected tags"
                />
                <Typography variant="h4" component="div">
                  {(tagMetrics.averageConfidence * 100).toFixed(1)}%
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Selected tags only
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Tags with Limiters */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                With Limiters
              </Typography>
              <Typography variant="h4" component="div">
                {tagMetrics.tagsWithLimiters}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {tagMetrics.tagsWithoutLimiters} without limiters
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Confidence Distribution */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          🎯 Confidence Distribution
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="success.main">
                {tagMetrics.highConfidenceTags}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                High Confidence (≥80%)
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="warning.main">
                {tagMetrics.mediumConfidenceTags}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Medium Confidence (50-79%)
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="error.main">
                {tagMetrics.lowConfidenceTags}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Low Confidence (&lt;50%)
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Insights */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          💡 Summary Insights
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">
            • <strong>{tagMetrics.selectedTags}</strong> out of <strong>{tagMetrics.totalTags}</strong> competencies are selected ({tagMetrics.selectedPercentage.toFixed(1)}%)
          </Typography>

          <Typography variant="body2">
            • <strong>{tagMetrics.tagsWithLimiters}</strong> selected tags have specific limiters applied
          </Typography>

          <Typography variant="body2">
            • Average confidence score is <strong>{(tagMetrics.averageConfidence * 100).toFixed(1)}%</strong>
            {tagMetrics.averageConfidence >= 0.8 && " (Excellent!)"}
            {tagMetrics.averageConfidence >= 0.5 && tagMetrics.averageConfidence < 0.8 && " (Good)"}
            {tagMetrics.averageConfidence < 0.5 && " (Needs Review)"}
          </Typography>

          {tagMetrics.highConfidenceTags > 0 && (
            <Typography variant="body2">
              • <strong>{tagMetrics.highConfidenceTags}</strong> tags have high confidence (≥80%)
            </Typography>
          )}

          {tagMetrics.lowConfidenceTags > 0 && (
            <Typography variant="body2" color="warning.main">
              • <strong>{tagMetrics.lowConfidenceTags}</strong> tags have low confidence (&lt;50%) - consider reviewing
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
}